import { useEffect, useState } from 'react';
import {
  Users,
  CalendarDays,
  TrendingUp,
  Target,
  Clock,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Dumbbell,
  UserCheck,
  GraduationCap,
} from 'lucide-react';
import {
  LineChart,
  Line,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, subMonths, startOfMonth, startOfWeek, endOfWeek, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabase } from '../services/supabase';
import { ESTADOS_COBRADOS, eurosCobradosEntre, type PedidoCobrado } from '../lib/ingresos';
import {
  ESTADO_SUSCRITO,
  alumnosSuscritos,
  type FilaDeSuscripcion,
} from '../lib/suscritos';
import {
  serieDeMatriculas,
  type PedidoConLineas,
  type PuntoDeMatriculas,
} from '../lib/matriculas';
import {
  CREDITO_PF,
  alumnosDePFEntre,
  seriePF,
  type CreditoDePF,
} from '../lib/preparacionFisica';
import AvisosPanel from '../components/AvisosPanel';
import TareasAdminPanel from '../components/TareasAdminPanel';
import AvisosClubPanel from '../components/AvisosClubPanel';

// Shapes intermedios de las 8 queries de `loadDashboard`. Cada query pide
// un Pick estrecho del Row generado; safeQuery devuelve `T[] | null`.
type EnrollmentUserId = { user_id: string };
type SessionId = { id: string };
type WaitlistId = { id: string };
type SessionCapacity = { current_count: number | null; max_students: number | null };
type EnrollmentDate = { enrolled_at: string };


interface KPIs {
  alumnosActivos6m: number;
  alumnosActivos6mAnt: number;
  sesionesEstaSemana: number;
  alumnosSuscritos: number;
  alumnosPF: number;
  alumnosPFAnterior: number;
  ingresosMes: number;
  ingresosMesAnterior: number;
  ocupacionMedia: number;
  waitlistTotal: number;
}

interface MonthlyPoint {
  mes: string;
  valor: number;
}
interface MonthlyDualPoint {
  mes: string;
  inscripciones: number;
  gym: number;
}
const DEFAULT_KPIS: KPIs = {
  alumnosActivos6m: 0,
  alumnosActivos6mAnt: 0,
  sesionesEstaSemana: 0,
  alumnosSuscritos: 0,
  alumnosPF: 0,
  alumnosPFAnterior: 0,
  ingresosMes: 0,
  ingresosMesAnterior: 0,
  ocupacionMedia: 0,
  waitlistTotal: 0,
};

// Helper genérico: normaliza el resultado de una query de Supabase. El
// generic `T` se infiere del shape declarado en cada llamada — supabase-js sin
// <Database> infiere `data` como el Pick que pidió el select, así que el doble
// cast vía `unknown` es necesario por consulta.
//
// [PR 0 · lecturas mudas] Este helper se llamaba `safe` y no lo era. Devolvía
// `{ data, count }` —sin `error`, que ni aparecía en el tipo— envuelto en un
// `try/catch`. El `try/catch` no captura nada: postgrest-js no rechaza nunca,
// devuelve `{ data: null, error }`. O sea que las diez consultas del dashboard
// tiraban el error en el propio helper, y un fallo de RLS o un 300 PGRST201
// llegaban a la pantalla como ceros. El único aviso, `sinConexion`, salta por
// un `setTimeout` de 15 s, así que un error que vuelve al instante —que son
// todos los que no son de red— no lo levantaba nadie.
//
// Ahora el error sale, y `loadDashboard` lo cuenta. El `catch` se queda por si
// algún día `shouldThrowOnError` cambia, pero no es de donde vienen los
// errores de verdad.
// El doble cast de cada consulta tenía que repetir la forma de la respuesta, y
// al repetirla se dejaba fuera el `error`: un tipo que no lo declara hace que
// `res.error` sea `undefined` SIEMPRE, así que el helper habría seguido mudo
// aunque el `error` viniera en la respuesta de verdad. Con el alias, la forma
// se escribe una vez y no se puede olvidar la mitad.
type RespQ<T> = PromiseLike<{
  data: T | null;
  count?: number | null;
  error: { message: string } | null;
}>;

async function safeQuery<T>(
  fn: () => PromiseLike<{
    data: T | null;
    count?: number | null;
    error?: { message: string } | null;
  }>
): Promise<{ data: T | null; count: number | null; error: string | null }> {
  try {
    const res = await fn();
    return {
      data: res.data ?? null,
      count: res.count ?? null,
      error: res.error ? res.error.message || 'Error desconocido' : null,
    };
  } catch (e) {
    return { data: null, count: null, error: e instanceof Error ? e.message : 'Error desconocido' };
  }
}

export default function DashboardPage() {
  const [kpis, setKpis] = useState<KPIs>(DEFAULT_KPIS);
  const [ingresos, setIngresos] = useState<MonthlyPoint[]>([]);
  const [evolucionInscripciones, setEvolucionInscripciones] = useState<MonthlyDualPoint[]>([]);
  const [matriculas, setMatriculas] = useState<PuntoDeMatriculas[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [sinConexion, setSinConexion] = useState(false);
  // Un KPI a cero porque la consulta falló se lee igual que un KPI a cero de
  // verdad. `sinConexion` solo cubre el timeout; esto cubre el resto.
  const [consultasFallidas, setConsultasFallidas] = useState(0);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setDataLoading(true);
    setSinConexion(false);
    setConsultasFallidas(0);

    const timeoutId = setTimeout(() => {
      setDataLoading(false);
      setSinConexion(true);
    }, 15000);

    try {
      const now = new Date();
      // Los meses se manejan como intervalos medio abiertos `[inicio, fin)`.
      // Con `endOfMonth` + `lte` un pedido sellado a las 23:59:59.999 del
      // último día contaba en dos meses a la vez.
      const mesActualStart = startOfMonth(now);
      const mesSiguienteStart = startOfMonth(addMonths(now, 1));
      const mesAnteriorStart = startOfMonth(subMonths(now, 1));
      const seisMesesStart = startOfMonth(subMonths(now, 5));
      // Los seis meses del eje X, usados por las dos series mensuales.
      const mesesDelGrafico = Array.from({ length: 6 }, (_, i) =>
        startOfMonth(subMonths(now, 5 - i))
      );
      const etiquetaDelMes = (m: Date) => format(m, 'MMM yy', { locale: es });

      const seisMesesAtras = subMonths(now, 6).toISOString();
      const doceMesesAtras = subMonths(now, 12).toISOString();

      // ⚠️ LA SEMANA EMPIEZA EN LUNES. Lo de antes era
      // `semanaStart = now - now.getDay()`, o sea DOMINGO, y acababa en sábado.
      // Un domingo —medido el 4-oct-2026, que lo era— el KPI mostraba la semana
      // que EMPEZABA ese día y se dejaba fuera los seis días recién pasados.
      // Que la cifra saliera igual era casualidad: solo hay sesiones los lunes,
      // y cada ventana pillaba un lunes distinto.
      const semanaStart = startOfWeek(now, { weekStartsOn: 1 });
      const semanaEnd = endOfWeek(now, { weekStartsOn: 1 });

      // ⚠️ Y SE COMPARAN COMO DÍA DE CALENDARIO, NO EN UTC. `sessions.date` es
      // un `date`: un día del calendario, sin huso. `startOfWeek` devuelve
      // medianoche LOCAL, y `toISOString().slice(0,10)` sobre esa medianoche
      // resta el huso — en España (CET/CEST, +1/+2) da SIEMPRE el día anterior.
      // Medido: el lunes 28-sep salía como `2026-09-27`.
      //
      // El código viejo tenía este fallo ADEMÁS del de empezar en domingo, y
      // los dos a la vez: la ventana real era de sábado a viernes.
      const diaDe = (d: Date) => format(d, 'yyyy-MM-dd');

      // KPI: Alumnos activos (con inscripción confirmada en los últimos 6 meses)
      const activos6mRes = await safeQuery<EnrollmentUserId[]>(
        () =>
          supabase
            .from('enrollments')
            .select('user_id')
            .eq('status', 'confirmed')
            .gte('enrolled_at', seisMesesAtras) as unknown as RespQ<EnrollmentUserId[]>
      );
      const activos6mSet = new Set<string>((activos6mRes.data ?? []).map((e) => e.user_id));

      // Periodo anterior (6-12 meses) para comparar
      const activos6mAntRes = await safeQuery<EnrollmentUserId[]>(
        () =>
          supabase
            .from('enrollments')
            .select('user_id')
            .eq('status', 'confirmed')
            .gte('enrolled_at', doceMesesAtras)
            .lt('enrolled_at', seisMesesAtras) as unknown as RespQ<EnrollmentUserId[]>
      );
      const activos6mAntSet = new Set<string>((activos6mAntRes.data ?? []).map((e) => e.user_id));

      // Sesiones de esta semana
      const sesionesRes = await safeQuery<SessionId[]>(
        () =>
          supabase
            .from('sessions')
            .select('id')
            .gte('date', diaDe(semanaStart))
            .lte('date', diaDe(semanaEnd))
            .eq('status', 'scheduled') as unknown as RespQ<SessionId[]>
      );
      const sesionesCount = sesionesRes.data?.length ?? 0;

      // Waitlist total
      const waitlistRes = await safeQuery<WaitlistId[]>(
        () => supabase.from('waitlist').select('id') as unknown as RespQ<WaitlistId[]>
      );
      const waitlistCount = waitlistRes.data?.length ?? 0;

      // ── ALUMNOS SUSCRITOS · personas, no filas ──────────────────────────
      // El dashboard no tenía ningún contador de suscritos: lo único que decía
      // «Suscripciones» era el aviso de caducidad, y contaba filas. Las cuatro
      // cifras que se confunden, medidas el 4-oct-2026: 25 filas en la tabla,
      // 16 alumnos que compraron alguna vez, 49 si se suman los `term_months`
      // y 14 alumnos suscritos de verdad hoy.
      //
      // Aquí se filtra SOLO por estado y la caducidad la decide
      // `alumnosSuscritos` sobre las filas: la regla de qué es «viva» vive en
      // un sitio, con sus pruebas, en vez de repetida entre el filtro de la
      // consulta y el cliente. Ver `lib/suscritos.ts`.
      const suscripcionesRes = await safeQuery<FilaDeSuscripcion[]>(
        () =>
          supabase
            .from('user_subscriptions')
            .select('user_id, status, expires_at')
            .eq('status', ESTADO_SUSCRITO) as unknown as RespQ<FilaDeSuscripcion[]>
      );
      const suscritos = alumnosSuscritos(suscripcionesRes.data ?? [], now);

      // ── INGRESOS · una sola lectura para los tres sitios ────────────────
      // Antes eran TRES consultas a `user_credits` (mes, mes anterior y seis
      // meses) y las tres contaban mal: `user_credits` tiene cuatro filas en
      // producción y el dinero entra por `orders`. Ahora es UNA lectura de los
      // pedidos cobrados de los últimos seis meses, y de ahí salen el KPI del
      // mes, el del mes anterior y la serie del gráfico — así los tres números
      // de la pantalla no pueden discrepar entre sí.
      //
      // `.in('status', ...)` con la lista blanca de `lib/ingresos.ts`, que es la
      // misma que usa `/estadisticas`. Ver ese fichero para por qué `delivered`
      // entra y por qué no vale filtrar por `payment_intent`.
      const pedidosRes = await safeQuery<PedidoCobrado[]>(
        () =>
          supabase
            .from('orders')
            .select('status, total_cents, created_at')
            .in('status', [...ESTADOS_COBRADOS])
            .gte('created_at', seisMesesStart.toISOString()) as unknown as RespQ<PedidoCobrado[]>
      );
      const pedidosCobrados = pedidosRes.data ?? [];

      // ── MATRÍCULAS PAGADAS · mensual y acumulado ────────────────────────
      // Lectura aparte de la de ingresos, y por dos razones:
      //
      //   · necesita `items`, que es el jsonb con las líneas del pedido —
      //     cargarlo en la lectura de los ingresos sería traer el detalle de
      //     todos los pedidos para sumar cuatro euros;
      //   · y NO lleva filtro de fecha, porque el acumulado es acumulado: tiene
      //     que contar también lo anterior a la ventana de seis meses. Son 29
      //     pedidos cobrados y 47 kB de `items` en toda la base.
      //
      // La mitad de las matrículas no se ven en la línea del pedido: van dentro
      // de un pack, en `components.items[]`. Ver `lib/matriculas.ts`.
      const matriculasRes = await safeQuery<PedidoConLineas[]>(
        () =>
          supabase
            .from('orders')
            .select('status, created_at, items', { count: 'exact' })
            .in('status', [...ESTADOS_COBRADOS]) as unknown as RespQ<PedidoConLineas[]>
      );
      // PostgREST trunca por `max-rows` SIN avisar. Un acumulado calculado
      // sobre media tabla es un número más bajo que el de verdad y nada en la
      // pantalla lo distingue, así que una lectura a medias se trata como una
      // lectura fallida: mejor «no ha respondido» que una cifra menor.
      const matriculasTruncado =
        matriculasRes.count !== null && (matriculasRes.data?.length ?? 0) < matriculasRes.count;

      // ── OCUPACIÓN MEDIA · solo lo que no ha pasado ─────────────────────
      // `status = 'scheduled'` NO quiere decir «futura»: una sesión que ya
      // ocurrió y a la que nadie cambió el estado sigue ahí. Medido: 33 de las
      // 115 `scheduled` de producción son de días pasados, y mezclarlas
      // convierte el KPI en una media de historia y previsión a la vez. La
      // pregunta que contesta esta tarjeta es «cómo vienen de llenas las
      // clases», o sea las que quedan.
      const sesionesOcupRes = await safeQuery<SessionCapacity[]>(
        () =>
          supabase
            .from('sessions')
            .select('current_count, max_students')
            .eq('status', 'scheduled')
            .gte('date', diaDe(now)) as unknown as RespQ<SessionCapacity[]>
      );
      let totalSlots = 0,
        totalOcupados = 0;
      (sesionesOcupRes.data ?? []).forEach((s) => {
        totalSlots += s.max_students ?? 0;
        totalOcupados += s.current_count ?? 0;
      });

      // Ingresos últimos 6 meses, de la MISMA lectura que los dos KPIs.
      // Se recorre mes a mes con intervalos `[inicio, siguiente)` en vez de
      // agrupar por una etiqueta de texto: con `format(m,'MMM yy')` como clave,
      // dos meses del mismo nombre en años distintos se habrían sumado juntos.
      setIngresos(
        mesesDelGrafico.map((m) => ({
          mes: etiquetaDelMes(m),
          valor: eurosCobradosEntre(pedidosCobrados, m, startOfMonth(addMonths(m, 1))),
        }))
      );

      setMatriculas(
        matriculasTruncado || matriculasRes.error != null
          ? []
          : serieDeMatriculas(matriculasRes.data ?? [], mesesDelGrafico, etiquetaDelMes)
      );

      // ── EVOLUCIÓN MENSUAL · inscripciones y PF ──────────────────────────
      // Los dos cubos se llenaban agrupando por la ETIQUETA del mes
      // (`format(m,'MMM yy')`), así que dos septiembres de años distintos caían
      // en el mismo. Ahora se recorre por intervalo medio abierto, como las
      // otras dos series de la pantalla.
      const enrollSeis = await safeQuery<EnrollmentDate[]>(
        () =>
          supabase
            .from('enrollments')
            .select('enrolled_at')
            .eq('status', 'confirmed')
            .gte('enrolled_at', seisMesesStart.toISOString()) as unknown as RespQ<EnrollmentDate[]>
      );
      // PF: créditos de acceso al gimnasio. SE CUENTAN ALUMNOS, no filas: hay
      // 3 filas y 2 alumnos en producción, y la serie se titula «alumnos».
      const gymSeis = await safeQuery<CreditoDePF[]>(
        () =>
          supabase
            .from('user_credits')
            .select('user_id, purchase_date')
            .eq('credit_type', CREDITO_PF)
            .gte('purchase_date', seisMesesStart.toISOString()) as unknown as RespQ<CreditoDePF[]>
      );
      const creditosPF = gymSeis.data ?? [];
      const inscripciones = enrollSeis.data ?? [];
      const serieDePF = seriePF(creditosPF, mesesDelGrafico, etiquetaDelMes, (m) =>
        startOfMonth(addMonths(m, 1))
      );
      setEvolucionInscripciones(
        mesesDelGrafico.map((m, i) => {
          const desde = m.getTime();
          const hasta = startOfMonth(addMonths(m, 1)).getTime();
          return {
            mes: etiquetaDelMes(m),
            inscripciones: inscripciones.filter((e) => {
              const t = new Date(e.enrolled_at).getTime();
              return t >= desde && t < hasta;
            }).length,
            gym: serieDePF[i].alumnos,
          };
        })
      );

      // Los diez errores, contados. Un KPI a cero por un fallo de lectura y un
      // KPI a cero de verdad se ven igual en pantalla, así que la única forma
      // de distinguirlos es decir cuántas consultas no respondieron.
      setConsultasFallidas(
        [
          activos6mRes,
          activos6mAntRes,
          sesionesRes,
          waitlistRes,
          suscripcionesRes,
          pedidosRes,
          { error: matriculasRes.error ?? (matriculasTruncado ? 'lectura a medias' : null) },
          sesionesOcupRes,
          enrollSeis,
          gymSeis,
        ].filter((r) => r.error != null).length
      );

      setKpis({
        alumnosActivos6m: activos6mSet.size,
        alumnosActivos6mAnt: activos6mAntSet.size,
        sesionesEstaSemana: sesionesCount,
        alumnosSuscritos: suscritos,
        // PF, el mes en curso y el anterior. Son dos números absolutos y no un
        // porcentaje a propósito: con 2 alumnos, un «-100 %» dice menos que
        // «0, mes anterior 2» — y de 0 a 0 el porcentaje no existe.
        alumnosPF: alumnosDePFEntre(creditosPF, mesActualStart, mesSiguienteStart),
        alumnosPFAnterior: alumnosDePFEntre(creditosPF, mesAnteriorStart, mesActualStart),
        ingresosMes: eurosCobradosEntre(pedidosCobrados, mesActualStart, mesSiguienteStart),
        ingresosMesAnterior: eurosCobradosEntre(pedidosCobrados, mesAnteriorStart, mesActualStart),
        ocupacionMedia: totalSlots > 0 ? Math.round((totalOcupados / totalSlots) * 100) : 0,
        waitlistTotal: waitlistCount,
      });
    } catch {
      // Mantener los valores anteriores / ceros
    } finally {
      clearTimeout(timeoutId);
      setDataLoading(false);
    }
  }

  const pctAlumnos =
    kpis.alumnosActivos6mAnt > 0
      ? Math.round(
          ((kpis.alumnosActivos6m - kpis.alumnosActivos6mAnt) / kpis.alumnosActivos6mAnt) * 100
        )
      : 0;
  const pctIngresos =
    kpis.ingresosMesAnterior > 0
      ? Math.round(((kpis.ingresosMes - kpis.ingresosMesAnterior) / kpis.ingresosMesAnterior) * 100)
      : 0;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Barra de estado */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {dataLoading && (
            <>
              <div className="w-3 h-3 border border-gold-primary/40 border-t-gold-primary rounded-full animate-spin" />
              <span className="text-text-secondary text-xs font-inter">Actualizando datos…</span>
            </>
          )}
          {sinConexion && (
            <span className="text-text-secondary text-xs font-inter bg-bg-elevated px-3 py-1 rounded-full border border-bg-border">
              Sin conexión con Supabase — datos no disponibles
            </span>
          )}
          {!sinConexion && consultasFallidas > 0 && (
            <span
              className="text-error text-xs font-inter bg-error/10 px-3 py-1 rounded-full"
              data-testid="dashboard-consultas-fallidas"
            >
              {consultasFallidas === 1
                ? 'Una consulta no ha respondido: hay cifras incompletas.'
                : `${consultasFallidas} consultas no han respondido: hay cifras incompletas.`}
            </span>
          )}
        </div>
        {!dataLoading && (
          <button
            onClick={loadDashboard}
            className="text-text-secondary hover:text-gold-primary transition-colors"
            title="Recargar datos"
          >
            <RefreshCw size={14} />
          </button>
        )}
      </div>

      {/* Sección 1 — Tareas pendientes de ADMINISTRACIÓN (requieren acción) */}
      <TareasAdminPanel />

      {/* Sección 2 — Tareas pendientes de los ENTRENADORES (informativo) */}
      <AvisosPanel />

      {/* Sección 3 — Avisos sin acción requerida (próximos a caducar) */}
      <AvisosClubPanel />

      {/* Sección 4 — KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <KPICard
          title="Alumnos activos (6m)"
          value={kpis.alumnosActivos6m}
          icon={<Users size={20} className="text-gold-primary" />}
          delta={pctAlumnos}
          loading={dataLoading}
        />
        <KPICard
          title="Sesiones esta semana"
          value={kpis.sesionesEstaSemana}
          icon={<CalendarDays size={20} className="text-gold-primary" />}
          loading={dataLoading}
        />
        <KPICard
          title="Alumnos suscritos"
          value={kpis.alumnosSuscritos}
          icon={<UserCheck size={20} className="text-gold-primary" />}
          loading={dataLoading}
        />
        <KPICard
          title="Preparación Física (mes)"
          value={kpis.alumnosPF}
          icon={<Dumbbell size={20} style={{ color: '#A3E635' }} />}
          anterior={kpis.alumnosPFAnterior}
          loading={dataLoading}
        />
        <KPICard
          title="Ingresos este mes"
          value={kpis.ingresosMes}
          icon={<TrendingUp size={20} className="text-gold-primary" />}
          delta={pctIngresos}
          prefix="€"
          decimals
          loading={dataLoading}
        />
        <KPICard
          title="Ocupación media"
          value={kpis.ocupacionMedia}
          icon={<Target size={20} className="text-gold-primary" />}
          suffix="%"
          loading={dataLoading}
        />
        <KPICard
          title="En lista de espera"
          value={kpis.waitlistTotal}
          icon={<Clock size={20} className="text-gold-primary" />}
          loading={dataLoading}
        />
      </div>

      {/* Gráficos fila 1: Ingresos + Evolución mensual */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Ingresos — últimos 6 meses
          </h3>
          {ingresos.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={ingresos}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="mes" tick={{ fill: '#A0A0A0', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A0A0A0', fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2A2A2A',
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(v: number) => [`€${v.toFixed(2)}`, 'Ingresos']}
                />
                <Line
                  type="monotone"
                  dataKey="valor"
                  stroke="#FFF200"
                  strokeWidth={2}
                  dot={{ fill: '#FFF200' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center">
              <p className="text-text-secondary text-sm">
                {dataLoading ? 'Cargando…' : 'Sin datos'}
              </p>
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Evolución mensual — inscripciones
          </h3>
          {evolucionInscripciones.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={evolucionInscripciones}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="mes" tick={{ fill: '#A0A0A0', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A0A0A0', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2A2A2A',
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#A0A0A0' }} />
                <Line
                  type="monotone"
                  dataKey="inscripciones"
                  name="Sesiones"
                  stroke="#FFF200"
                  strokeWidth={2}
                  dot={{ fill: '#FFF200' }}
                />
                <Line
                  type="monotone"
                  dataKey="gym"
                  name="PF"
                  stroke="#A3E635"
                  strokeWidth={2}
                  dot={{ fill: '#A3E635' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center">
              <p className="text-text-secondary text-sm">
                {dataLoading ? 'Cargando…' : 'Sin datos'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Matrículas pagadas — mensual y acumulado */}
      <div className="card" data-testid="dashboard-matriculas">
        <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4 flex items-center gap-2">
          <GraduationCap size={16} className="text-gold-primary" />
          Matrículas pagadas — mensual y acumulado
        </h3>
        {matriculas.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={matriculas}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
              <XAxis dataKey="mes" tick={{ fill: '#A0A0A0', fontSize: 11 }} />
              <YAxis tick={{ fill: '#A0A0A0', fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #2A2A2A',
                  borderRadius: 8,
                }}
                labelStyle={{ color: '#fff' }}
                formatter={(v: number) => `${v} matrícula${v === 1 ? '' : 's'}`}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: '#A0A0A0' }} />
              <Bar dataKey="delMes" name="Del mes" fill="#FFF200" radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="acumulado"
                name="Acumulado"
                stroke="#60A5FA"
                strokeWidth={2}
                dot={{ fill: '#60A5FA' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div
            className="h-[240px] flex items-center justify-center"
            data-testid="dashboard-matriculas-sin-datos"
          >
            <p className="text-text-secondary text-sm">{dataLoading ? 'Cargando…' : 'Sin datos'}</p>
          </div>
        )}
      </div>

    </div>
  );
}

function KPICard({
  title,
  value,
  icon,
  delta,
  anterior,
  prefix = '',
  suffix = '',
  decimals = false,
  loading = false,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  delta?: number;
  /**
   * El valor del mes anterior, en absoluto. Para cifras pequeñas un porcentaje
   * miente por los dos lados: de 2 a 0 es «-100 %» y de 0 a 0 no existe.
   */
  anterior?: number;
  prefix?: string;
  suffix?: string;
  decimals?: boolean;
  loading?: boolean;
}) {
  const displayValue = decimals ? value.toFixed(2) : value;
  return (
    <div className="kpi-card">
      <div className="flex items-center justify-between">
        <p className="label">{title}</p>
        {icon}
      </div>
      {loading ? (
        <div className="h-8 w-16 bg-bg-elevated rounded animate-pulse mt-1" />
      ) : (
        <p className="font-oswald text-2xl text-white">
          {prefix}
          {displayValue}
          {suffix}
        </p>
      )}
      {anterior !== undefined && !loading && (
        <div className="text-xs font-inter text-text-secondary">
          Mes anterior: {anterior}
        </div>
      )}
      {delta !== undefined && !loading && (
        <div
          className={`flex items-center gap-1 text-xs font-inter ${delta >= 0 ? 'text-success' : 'text-error'}`}
        >
          {delta >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {Math.abs(delta)}% vs periodo anterior
        </div>
      )}
    </div>
  );
}
